// src/components/ScrollProgress.jsx
import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollProgress
 * - 페이지(또는 특정 컨테이너)의 스크롤 진행률을 가로 바 형태로 표시
 *
 * Props:
 * - position: "top" | "bottom" | "absolute"  (기본 top)
 *   - "absolute": containerRef 기준 절대배치(히어로 하단 등에 붙일 때)
 * - height: number (px)  기본 5
 * - colorStart: string   기본 "#2f6f5c"
 * - colorEnd: string     기본 "#5ca68e"
 * - easing: number       보간 정도(0.08~0.2 권장) 기본 0.12
 * - className: string    추가 클래스
 * - containerRef: React.RefObject<HTMLElement>
 *    - 지정하면 해당 컨테이너의 스크롤 진행률을 계산 (없으면 window 기준)
 * - minScroll: number    진행률 표시 최소 스크롤 높이(px). 기본 0 (짧은 페이지에서 숨김용)
 * - hiddenWhenNoScroll: boolean  true면 스크롤할 여유가 없을 때 숨김 (기본 true)
 */
const ScrollProgress = ({
  position = 'top',
  height = 5,
  colorStart = '#2f6f5c',
  colorEnd = '#5ca68e',
  easing = 0.12,
  className = '',
  containerRef = null,
  minScroll = 0,
  hiddenWhenNoScroll = true,
}) => {
  const [percent, setPercent] = useState(0);
  const targetRef = useRef(0);
  const currentRef = useRef(0);
  const rafRef = useRef(null);

  const isWindow = typeof window !== 'undefined';

  const calcTarget = () => {
    if (!isWindow) return 0;

    // window 기준
    if (!containerRef?.current) {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const total = Math.max(docHeight - winHeight, 0);
      if (total <= minScroll) return 0;
      return (scrollTop / total) * 100;
    }

    // 특정 컨테이너 기준
    const el = containerRef.current;
    const scrollTop = el.scrollTop;
    const total = Math.max(el.scrollHeight - el.clientHeight, 0);
    if (total <= minScroll) return 0;
    return (scrollTop / total) * 100;
  };

  useEffect(() => {
    if (!isWindow) return;

    const updateTarget = () => {
      targetRef.current = calcTarget();
    };

    const tick = () => {
      // prefers-reduced-motion: reduce → 즉시 반응
      const prefersReduced =
        isWindow && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (prefersReduced) {
        currentRef.current = targetRef.current;
      } else {
        currentRef.current += (targetRef.current - currentRef.current) * easing;
      }

      const clamped = Math.max(0, Math.min(100, currentRef.current));
      setPercent(clamped);
      rafRef.current = requestAnimationFrame(tick);
    };

    // 바인딩
    if (containerRef?.current) {
      containerRef.current.addEventListener('scroll', updateTarget, { passive: true });
      containerRef.current.addEventListener('resize', updateTarget);
    } else {
      window.addEventListener('scroll', updateTarget, { passive: true });
      window.addEventListener('resize', updateTarget);
    }

    // 초기값
    updateTarget();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (containerRef?.current) {
        containerRef.current.removeEventListener('scroll', updateTarget);
        containerRef.current.removeEventListener('resize', updateTarget);
      } else {
        window.removeEventListener('scroll', updateTarget);
        window.removeEventListener('resize', updateTarget);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, easing, minScroll]);

  // 숨김 처리 (스크롤 여유 없음)
  const noScroll =
    hiddenWhenNoScroll &&
    ((containerRef?.current && containerRef.current.scrollHeight - containerRef.current.clientHeight <= minScroll) ||
      (!containerRef?.current && isWindow && document.documentElement.scrollHeight - window.innerHeight <= minScroll));

  // 위치 클래스 구성
  const posClass = position === 'bottom' ? 'is-bottom' : position === 'absolute' ? 'is-absolute' : 'is-top';

  return (
    <div
      className={`scroll-progress ${posClass} ${noScroll ? 'is-hidden' : ''} ${className}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      style={{
        // CSS 변수로 높이/색 제어
        ['--progress-height']: `${height}px`,
        ['--progress-color-start']: colorStart,
        ['--progress-color-end']: colorEnd,
        // absolute 모드에서 containerRef 기준 배치
        ...(position === 'absolute' && containerRef?.current
          ? {
              position: 'absolute',
              top: 'auto',
              bottom: 0,
              left: 0,
              width: '100%',
            }
          : {}),
      }}
    >
      <div className="scroll-progress__bar" style={{ transform: `scaleX(${percent / 100})` }} />
    </div>
  );
};

export default ScrollProgress;
